/**
 * Where the API is, when it is not this page's origin.
 *
 * In a browser tab the answer is nothing at all: one process serves the SPA and
 * `/api`, so every path is relative and the session cookie is same-origin. Inside
 * the installed app the answer must be a URL, because the WebView serves the bundled
 * app from `https://localhost` and *that* is this page's origin - a relative
 * `/api/sessions` is a request for a file in the bundle, which the shell's own asset
 * loader answers with `index.html`. The client then reads a 200 that is not JSON,
 * which is precisely the error a phone showed before this existed.
 *
 * Two consequences, both handled here rather than at every call site:
 *
 *  - **An absolute URL needs credentials.** `credentials: 'include'` is what sends
 *    the session cookie to another origin; `same-origin` (right for a tab) silently
 *    drops it. The server has to agree: see `allowShellOrigins` and the `SameSite`
 *    choice in the API.
 *  - **The origin comes from the APK, not the bundle.** It is read from the shell's
 *    `AppConfig` plugin at launch, so a web bundle downloaded over the air can never
 *    lose it - a bundle built without knowing your host would otherwise sign every
 *    phone out with its own update.
 */

import type { CapacitorBridge } from '../ota/host.js';

/** No base: this page's own origin. The value every browser tab uses. */
export const SAME_ORIGIN = '';

/**
 * A usable API origin, or null for anything else.
 *
 * Absolute `http(s)` only, and only the origin: a value carrying a path would
 * silently produce `/app/api/...` requests, so the path is dropped rather than
 * appended to.
 */
export function normalizeApiOrigin(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (trimmed === '') return null;
  try {
    const url = new URL(trimmed);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
    return url.origin;
  } catch {
    return null;
  }
}

/** The URL to call for an API path, on this origin or on the one the app named. */
export function apiUrl(path: string, base: string = SAME_ORIGIN): string {
  return base === SAME_ORIGIN ? path : `${base}${path}`;
}

/** How to carry the session cookie to that URL. */
export function credentialsFor(base: string = SAME_ORIGIN): RequestCredentials {
  return base === SAME_ORIGIN ? 'same-origin' : 'include';
}

/** The shell's config plugin, as much of it as the web layer uses. */
export interface AppConfigPlugin {
  get(): Promise<{ apiOrigin?: unknown }>;
}

/**
 * The API origin for this launch: `''` in a browser tab, the named server in the
 * installed app, and `null` for an installed app that names none.
 *
 * Null is a distinct answer, not an empty one. A build without an origin cannot
 * reach anything, and pretending otherwise produces exactly the failure this file
 * exists to prevent - so the boot path shows what is missing instead of rendering an
 * app that can only report a parse error.
 */
export async function apiOriginFor(bridge: CapacitorBridge | null): Promise<string | null> {
  if (bridge === null) return SAME_ORIGIN;
  const plugin = bridge.plugin<Partial<AppConfigPlugin>>('AppConfig');
  // An older shell with no config plugin is an app that names no server, whatever
  // the build intended.
  if (plugin?.get === undefined) return null;
  try {
    return normalizeApiOrigin((await plugin.get()).apiOrigin);
  } catch {
    return null;
  }
}
